'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

import { useLoader, useToast } from '@/hooks'
import {
  checkRoomAvailability,
  sendConfirmationEmail,
  submitBooking,
  verifyGift
} from '@/libs/actions/redeem.action'
import { cookies } from '@/libs/cookie'
import { devWarn } from '@/libs/devLog'

import { useRedeemT } from '../i18n/useRedeemT'
import type {
  IBookingDetail,
  IBookingRequest,
  IGiftDetail,
  IRedeemDeeplink,
  IRedeemErrorVariant,
  IRedeemFilter,
  IRedeemQueryPrefill,
  IRedeemRecommend,
  IRedeemStage,
  IUsedGiftBooking
} from '../types/schema'
import { fromAvailability, fromBookingRecommend } from '../utils/recommend'
import { toSendMailPayload } from '../utils/sendMailPayload'
import {
  BOOKING_TOKEN_TTL_MS,
  type AvailabilityInput,
  type BookingFormValues,
  type VerifyFormValues
} from './redeem.schema'

const STORAGE_KEY = 'redeem.state'

interface PersistedState {
  stage: IRedeemStage
  giftDetail: IGiftDetail | null
  bookingToken: string | null
  tokenIssuedAt: number | null
  filter: IRedeemFilter | null
  bookingDetail: IBookingDetail | null
  verifiedFor: VerifyFormValues | null
}

const EMPTY: PersistedState = {
  stage: 'verify',
  giftDetail: null,
  bookingToken: null,
  tokenIssuedAt: null,
  filter: null,
  bookingDetail: null,
  verifiedFor: null
}

const sameGift = (a: VerifyFormValues | null | undefined, b: VerifyFormValues) =>
  a != null &&
  a.giftNumber.trim().toLowerCase() === b.giftNumber.trim().toLowerCase() &&
  a.giftFrom.trim().toLowerCase() === b.giftFrom.trim().toLowerCase()

interface Options {
  locale: string
  /** Decoded `?token=` payload, from the server component. */
  initialDeeplink: IRedeemDeeplink | null
  /** Plain-query prefill (`?date=&time=&branch=&guest=`), the secondary entry path. */
  initialPrefill: IRedeemQueryPrefill | null
}

/**
 * Owns the whole flow. `stage` is progressive disclosure on one page, not navigation — every
 * section stays mounted, just locked. Booking JWT lasts 60 min; age is checked on mount and at
 * submit, then the flow restarts with a notice — the legacy page failed silently instead.
 */
export function useRedeem({ locale, initialDeeplink, initialPrefill }: Options) {
  const t = useRedeemT()
  const loader = useLoader()
  const toast = useToast()

  const [state, setState] = useState<PersistedState>(EMPTY)
  const [hydrated, setHydrated] = useState(false)
  const [error, setError] = useState<IRedeemErrorVariant | null>(null)
  const [usedBooking, setUsedBooking] = useState<IUsedGiftBooking | null>(null)
  const [recommend, setRecommend] = useState<IRedeemRecommend | null>(null)
  const [sessionExpired, setSessionExpired] = useState(false)
  const [isVerifying, setIsVerifying] = useState(false)
  const [isBooking, setIsBooking] = useState(false)
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false)

  /** Survives across renders so the deeplink cannot auto-verify twice. */
  const autoVerified = useRef(false)

  /** Identifies the newest availability check, so a slow earlier one cannot overwrite its answer. */
  const availabilityRequestId = useRef(0)

  // ── Persistence ──────────────────────────────────────────────────────────
  // Hydrate in an effect, not initial state, so server and first client render agree. Mount-only —
  // one page load, one deeplink.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY)

      if (raw) {
        const saved = JSON.parse(raw) as PersistedState
        const expired =
          saved.tokenIssuedAt !== null && Date.now() - saved.tokenIssuedAt > BOOKING_TOKEN_TTL_MS

        // A link for another gift outranks this tab's saved state — otherwise auto-verify below
        // skips it (giftDetail already set) and the new gift is silently dropped.
        const supersededByDeeplink =
          initialDeeplink !== null && !sameGift(saved.verifiedFor, initialDeeplink)

        if (supersededByDeeplink) {
          sessionStorage.removeItem(STORAGE_KEY)
        } else if (expired && saved.stage === 'booking') {
          // Dead token mid-flow. It cannot be refreshed without re-verifying the gift.
          sessionStorage.removeItem(STORAGE_KEY)
          setSessionExpired(true)
        } else {
          setState(saved)
        }
      }
    } catch {
      // Corrupt or unavailable storage (private mode, quota) is not a failure — start clean.
      sessionStorage.removeItem(STORAGE_KEY)
    }

    setHydrated(true)
  }, [])

  useEffect(() => {
    if (!hydrated) return

    try {
      if (state.stage === 'verify' && !state.giftDetail) sessionStorage.removeItem(STORAGE_KEY)
      else sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state))
    } catch {
      // Storage full or blocked — the flow still works for this tab, just not across reloads.
    }
  }, [state, hydrated])

  const reset = useCallback(() => {
    setState(EMPTY)
    setError(null)
    setUsedBooking(null)
    setRecommend(null)
    try {
      sessionStorage.removeItem(STORAGE_KEY)
    } catch {
      /* nothing to clean up */
    }
  }, [])

  // ── Verify ───────────────────────────────────────────────────────────────
  const runVerify = useCallback(
    async (values: VerifyFormValues) => {
      setError(null)
      setUsedBooking(null)
      setSessionExpired(false)
      setIsVerifying(true)
      loader.on()

      try {
        const response = await verifyGift(values, locale)

        if (response.status === 'error') {
          setError(response.msg === 'RATE_LIMITED' ? 'rate-limited' : 'network')
          return false
        }

        if (response.status === 'failure') {
          if (response.data.gift_status === 'used') {
            setUsedBooking(response.data.detail)
          }
          setError(response.data.gift_status)
          return false
        }

        setState({
          stage: 'booking',
          giftDetail: response.data.d,
          bookingToken: response.data.token,
          tokenIssuedAt: Date.now(),
          filter: response.data.filter,
          bookingDetail: null,
          verifiedFor: values
        })

        return true
      } catch (error) {
        console.error('[redeem] verify failed', error)
        setError('network')

        return false
      } finally {
        setIsVerifying(false)
        loader.off()
      }
    },
    [locale, loader]
  )

  // Deeplink auto-verify, as the legacy `search('skip')` did. Skips the captcha on purpose: no
  // invisible puzzle exists, and the link came from the user's own gift email.
  useEffect(() => {
    if (!hydrated || autoVerified.current) return
    if (!initialDeeplink) return
    if (state.stage !== 'verify' || state.giftDetail) return

    autoVerified.current = true
    void runVerify(initialDeeplink).then((ok) => {
      if (!ok) return

      try {
        const url = new URL(window.location.href)
        if (!url.searchParams.has('token')) return

        url.searchParams.delete('token')
        window.history.replaceState(
          window.history.state,
          '',
          `${url.pathname}${url.search}${url.hash}`
        )
      } catch {
        /* cosmetic cleanup only — never disturb a verified flow */
      }
    })
  }, [hydrated, initialDeeplink, state.stage, state.giftDetail, runVerify])

  // ── Availability ─────────────────────────────────────────────────────────
  /**
   * Would the booking API take this slot? A "no" becomes the suggestion panel. Resolves true when
   * the booking may proceed — including "no answer", since the booking API is the authority.
   * `allowed_branch_ids` is the gift's own branch list, or a gift valid at three branches could be
   * offered a fourth.
   */
  const runAvailabilityCheck = useCallback(
    async (values: AvailabilityInput): Promise<boolean> => {
      const filter = state.filter
      if (!filter || !values.branch || !values.date || !values.time || !values.hours) return true

      const requestId = ++availabilityRequestId.current
      setIsCheckingAvailability(true)

      try {
        const response = await checkRoomAvailability({
          appointment_date: values.date,
          appointment_time: values.time,
          total_hour: [values.hours],
          branch_id: values.branch,
          guest: values.guest,
          INT_recommend: true,
          parity_mode: 'redeem',
          allowed_branch_ids: filter.branch.map((branch) => branch.branch_id)
        })

        // A newer check has already been issued; that one owns the answer.
        if (requestId !== availabilityRequestId.current) return true

        // Only an explicit "no" blocks: the internal-error exit omits `roomAvailable` entirely,
        // and the booking API is the authority anyway.
        if (!response || response.roomAvailable !== false) {
          setRecommend(null)
          return true
        }

        setRecommend(fromAvailability(response, filter.branch, values.branch))
        return false
      } finally {
        if (requestId === availabilityRequestId.current) setIsCheckingAvailability(false)
      }
    },
    [state.filter]
  )

  // ── Booking ──────────────────────────────────────────────────────────────
  const runBooking = useCallback(
    async (values: BookingFormValues, cityId: number) => {
      const tokenExpired =
        state.tokenIssuedAt === null || Date.now() - state.tokenIssuedAt > BOOKING_TOKEN_TTL_MS

      if (!state.bookingToken || tokenExpired) {
        setSessionExpired(true)
        reset()
        return false
      }

      setError(null)
      setRecommend(null)
      setIsBooking(true)
      loader.on()

      try {
        // Re-checked at submit: the last free room can go while the user types their name.
        if (!(await runAvailabilityCheck(values))) return false

        const payload: IBookingRequest = {
          yourName: values.yourName,
          yourEmail: values.yourEmail,
          yourPhone: values.yourPhone,
          remark: values.remark || '',
          city: cityId,
          branch: values.branch,
          date: values.date,
          time: values.time,
          hours: values.hours,
          guest: values.guest as 1 | 2,
          source: cookies.get<string>('source') || '',
          utm_content: cookies.get<string>('utm_content') || '',
          timevalue: '',
          token: state.bookingToken
        }

        const response = await submitBooking(payload, state.bookingToken, locale)

        if (response.status === 'error') {
          if (response.msg === 'SESSION_EXPIRED') {
            setSessionExpired(true)
            reset()
            return false
          }

          // "Never landed" vs "landed and threw". InsertBooking unwinds manually, not in a
          // transaction, so a server error can leave the gift used. Only `NETWORK` never went out.
          setError(response.msg === 'NETWORK' ? 'network' : 'booking-unconfirmed')
          return false
        }

        // The gift changed state between verify and booking.
        if (response.status === 'failure') {
          setError(response.data.gift_status)
          return false
        }

        // Only when the pre-flight was skipped or disagreed. Still worth showing.
        if (!response.data.create_status) {
          setRecommend(
            fromBookingRecommend(response.data.recommend, state.filter?.branch ?? [], values.branch)
          )
          return false
        }

        const detail = response.data.detail
        setState((prev) => ({ ...prev, stage: 'confirm', bookingDetail: detail }))

        // Booking is committed, so a mail failure must never read as a booking failure. Never
        // throws; returns whether sendmail.php accepted it. That POST is server-side and never
        // shows in DevTools, so the outcome is reported here. Success is dev-only.
        void sendConfirmationEmail(toSendMailPayload(detail, values.remark || ''))
          .then((sent) => {
            if (sent) {
              devWarn('[redeem] confirmation email sent for', detail.bpds_id)
            } else {
              console.warn('[redeem] confirmation email not sent for', detail.bpds_id)
            }
          })
          .catch(() => {})

        return true
      } catch (error) {
        console.error('[redeem] booking failed', error)
        setError('booking-unconfirmed')

        return false
      } finally {
        setIsBooking(false)
        loader.off()
      }
    },
    [
      state.bookingToken,
      state.tokenIssuedAt,
      state.filter,
      locale,
      loader,
      reset,
      runAvailabilityCheck
    ]
  )

  // ── Recommend ────────────────────────────────────────────────────────────
  /** Clears the panel once the user has taken its suggestion into the form. */
  const clearRecommend = useCallback(() => setRecommend(null), [])

  const notifySessionExpired = useCallback(() => {
    if (!sessionExpired) return
    toast.on(t('NOTICE_SESSION_EXPIRED'), { name: 'redeem-session', duration: 6 })
    setSessionExpired(false)
  }, [sessionExpired, toast, t])

  useEffect(() => {
    notifySessionExpired()
  }, [notifySessionExpired])

  return {
    // state
    hydrated,
    stage: state.stage,
    giftDetail: state.giftDetail,
    filter: state.filter,
    bookingDetail: state.bookingDetail,
    error,
    usedBooking,
    recommend,
    isVerifying,
    isBooking,
    isCheckingAvailability,
    initialDeeplink,
    initialPrefill,
    // actions
    runVerify,
    runBooking,
    runAvailabilityCheck,
    clearRecommend,
    clearError: useCallback(() => setError(null), []),
    reset
  }
}
